import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TutorialResource } from '../resource/tutorial.resource';
import { TutorialManagement, TutorialSettings } from '@maporium-workspace/shared';

@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  private tutorialsSubject = new BehaviorSubject<TutorialManagement[]>([]);
  private tutorialSettingsSubject = new BehaviorSubject<TutorialSettings | null>(null);

  public tutorials$ = this.tutorialsSubject.asObservable();
  public tutorialSettings$ = this.tutorialSettingsSubject.asObservable();

  constructor(private tutorialResource: TutorialResource) {}

  getTutorials(): Observable<TutorialSettings | null> {
    return this.tutorialResource.getTutorials().pipe(
      tap(settings => {
        this.tutorialSettingsSubject.next(settings);
        this.tutorialsSubject.next(settings?.tutorials || []);
      })
    );
  }

  getTutorialById(id: string): Observable<TutorialManagement | null> {
    return this.tutorialResource.getTutorialById(id);
  }

  createTutorial(tutorial: Omit<TutorialManagement, 'id' | 'createdAt' | 'updatedAt'>): Observable<TutorialManagement> {
    return this.tutorialResource.createTutorial(tutorial).pipe(
      tap(newTutorial => {
        const currentTutorials = this.tutorialsSubject.value;
        this.tutorialsSubject.next([...currentTutorials, newTutorial]);
        this.refreshTutorialSettings();
      })
    );
  }

  updateTutorial(id: string, updates: Partial<TutorialManagement>): Observable<TutorialManagement | null> {
    return this.tutorialResource.updateTutorial(id, updates).pipe(
      tap(updatedTutorial => {
        if (updatedTutorial) {
          const currentTutorials = this.tutorialsSubject.value;
          const index = currentTutorials.findIndex(t => t.id === id);
          if (index !== -1) {
            currentTutorials[index] = updatedTutorial;
            this.tutorialsSubject.next([...currentTutorials]);
            this.refreshTutorialSettings();
          }
        }
      })
    );
  }

  deleteTutorial(id: string): Observable<{ success: boolean }> {
    return this.tutorialResource.deleteTutorial(id).pipe(
      tap(response => {
        if (response.success) {
          const currentTutorials = this.tutorialsSubject.value;
          this.tutorialsSubject.next(currentTutorials.filter(t => t.id !== id));
          this.refreshTutorialSettings();
        }
      })
    );
  }

  toggleTutorialStatus(id: string): Observable<TutorialManagement | null> {
    return this.tutorialResource.toggleTutorialStatus(id).pipe(
      tap(updatedTutorial => {
        if (updatedTutorial) {
          const currentTutorials = this.tutorialsSubject.value;
          const index = currentTutorials.findIndex(t => t.id === id);
          if (index !== -1) {
            currentTutorials[index] = updatedTutorial;
            this.tutorialsSubject.next([...currentTutorials]);
            this.refreshTutorialSettings();
          }
        }
      })
    );
  }

  reorderTutorials(tutorialIds: string[]): Observable<TutorialManagement[]> {
    return this.tutorialResource.reorderTutorials(tutorialIds).pipe(
      tap(reorderedTutorials => {
        this.tutorialsSubject.next(reorderedTutorials);
        this.refreshTutorialSettings();
      })
    );
  }

  saveTutorialSettings(settings: TutorialSettings): Observable<TutorialSettings> {
    return this.tutorialResource.saveTutorialSettings(settings).pipe(
      tap(savedSettings => {
        this.tutorialSettingsSubject.next(savedSettings);
        this.tutorialsSubject.next(savedSettings.tutorials);
      })
    );
  }

  getActiveTutorials(): TutorialManagement[] {
    return this.tutorialsSubject.value.filter(tutorial => tutorial.isActive !== false);
  }

  getTutorialsByServicePlan(servicePlan: string): TutorialManagement[] {
    return this.getActiveTutorials().filter(tutorial => 
      this.isServicePlanEligible(tutorial.minServicePlanId, servicePlan)
    );
  }

  private isServicePlanEligible(requiredPlan: string, userPlan: string): boolean {
    const planHierarchy = ['explorer', 'creator', 'publisher', 'developer'];
    const requiredIndex = planHierarchy.indexOf(requiredPlan);
    const userIndex = planHierarchy.indexOf(userPlan);
    
    return requiredIndex !== -1 && userIndex !== -1 && userIndex >= requiredIndex;
  }

  private refreshTutorialSettings(): void {
    const currentSettings = this.tutorialSettingsSubject.value;
    if (currentSettings) {
      const updatedSettings = {
        ...currentSettings,
        tutorials: this.tutorialsSubject.value,
        lastUpdated: new Date()
      };
      this.tutorialSettingsSubject.next(updatedSettings);
    }
  }
}